import {
  PricingChannel,
  PricingConfigurationValues,
  PricingRate,
  PricingValues,
} from '@/services/Pricing/pricing-configuration.service';
import { formRowsToValues, valuesToFormRows } from './pricingFormMapping';

export const pricingValuesEqual = (a: PricingValues, b: PricingValues): boolean =>
  a.variable === b.variable && a.fixed === b.fixed && a.amex === b.amex;

// Round-trips values through the form representation so comparisons use the same
// precision the inputs display, avoiding false diffs from sub-cent/sub-percent noise.
export const normalizeToFormPrecision = (
  values: PricingConfigurationValues,
): PricingConfigurationValues => formRowsToValues(valuesToFormRows(values));

// Returns the next stored overrides after editing one country: drop the entry when
// it matches the baseline (so defaults apply), otherwise upsert the edited values.
export const computeNextValues = (
  existing: PricingConfigurationValues | undefined,
  baseline: PricingConfigurationValues,
  activeCountry: string,
  activeValues: PricingValues,
): PricingConfigurationValues => {
  const normalizedBaseline = normalizeToFormPrecision(baseline);
  const matchesBaseline =
    !!normalizedBaseline[activeCountry] &&
    pricingValuesEqual(activeValues, normalizedBaseline[activeCountry]);

  const nextValues: PricingConfigurationValues = { ...(existing ?? {}) };
  if (matchesBaseline) {
    delete nextValues[activeCountry];
  } else {
    nextValues[activeCountry] = activeValues;
  }
  return nextValues;
};

/**
 * The full rate list after editing one country on one channel. The save replaces the whole stored
 * list, so every other row (the untouched channel included) is carried through; a row equal to the
 * baseline is dropped so the level re-inherits.
 */
export const computeNextRates = (params: {
  existing: PricingRate[];
  baseline: PricingConfigurationValues;
  country: string;
  channel: PricingChannel;
  values: PricingValues;
}): PricingRate[] => {
  const { existing, baseline, country, channel, values } = params;
  // Named params: `country` and `channel` are both strings and would transpose silently.
  const others = existing.filter((rate) => !(rate.countryCode === country && rate.channel === channel));

  const normalizedBaseline = normalizeToFormPrecision(baseline);
  const inheritsBaseline =
    !!normalizedBaseline[country] && pricingValuesEqual(values, normalizedBaseline[country]);
  if (inheritsBaseline) return others;

  return [...others, { countryCode: country, channel, ...values }];
};
